import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { CompanyUserRole } from "@finora/types";
import { Button } from "@finora/ui";
import { api } from "../../../lib/api";
import type { SelectedPackage } from "./PackageStep";

const ROLE_LABELS: Record<string, string> = {
  OWNER: "Vlasnik",
  ADMIN: "Administrator",
  CASHIER: "Kasir",
  ACCOUNTANT: "Knjigovođa",
  VIEWER: "Samo pregled",
};

interface Props {
  selectedPackage: SelectedPackage | null;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Onboarding, korak "Korisnici" - STVARNA lista/dodavanje preko GET/POST
 * /users (isti endpoint kao AdminUsersPage). Napomena: trenutni API nema
 * email-invite/token tok - admin ovde direktno postavlja email+lozinku za
 * novog korisnika, isto kao u AdminUsersPage (vidi razgovor "Zadrži
 * trenutni način"). includedUsers iz izabranog paketa je prikazan kao
 * informativna napomena, ne kao tvrdo ograničenje (nema poseban
 * seat-limit endpoint još).
 */
export function UsersStep({ selectedPackage, onNext, onBack }: Props) {
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useQuery({
    queryKey: ["users-onboarding"],
    queryFn: () => api.listUsers(),
  });

  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<string>(CompanyUserRole.VIEWER);

  const createUser = useMutation({
    mutationFn: () => api.createUser({ fullName, email, password, role: role as CompanyUserRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-onboarding"] });
      setFullName("");
      setEmail("");
      setPassword("");
      setRole(CompanyUserRole.VIEWER);
      setShowForm(false);
    },
  });

  const included = selectedPackage?.includedUsers ?? null;

  return (
    <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Korisnici</p>
          <h1 className="mb-1 text-2xl font-bold text-slate-900">Dodajte korisnike u svoj tim</h1>
          <p className="text-sm text-slate-500">
            Dodajte korisnike koji će koristiti HAFiskal. Svakom korisniku možete dodeliti ulogu.
            {included !== null && ` Vaš paket uključuje do ${included} korisnika.`}
          </p>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setShowForm((v) => !v)}>
          <UserPlus className="h-4 w-4" /> Dodaj korisnika
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createUser.mutate();
          }}
          className="mb-4 grid gap-3 rounded-lg border border-border bg-app-bg p-4 md:grid-cols-4"
        >
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Ime i prezime"
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm focus:border-brand focus:outline-none"
            required
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm focus:border-brand focus:outline-none"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Lozinka (bar 6 karaktera)"
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm focus:border-brand focus:outline-none"
            required
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="h-10 rounded-lg border border-border bg-surface px-3 text-sm focus:border-brand focus:outline-none"
          >
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="md:col-span-4">
            {createUser.isError && (
              <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {createUser.error instanceof ApiError
                  ? createUser.error.response.message
                  : "Dodavanje korisnika nije uspelo."}
              </p>
            )}
            <Button type="submit" size="sm" disabled={createUser.isPending}>
              {createUser.isPending ? "Dodajem..." : "Sačuvaj korisnika"}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Učitavanje...</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-app-bg text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Ime i prezime</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Uloga</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users?.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-2.5 text-slate-900">{u.fullName}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-500">{ROLE_LABELS[u.role] ?? u.role}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        u.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {u.isActive ? "Aktivan" : "Neaktivan"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex justify-between border-t border-border pt-6">
        <Button variant="secondary" onClick={onBack}>
          ← Nazad
        </Button>
        <Button onClick={onNext}>Nastavi →</Button>
      </div>
    </div>
  );
}
